'use client';
import { FormikHelpers, FormikErrors } from 'formik';
import './SignupStep2Form.style.scss';
import Form, { BaseFormProps } from '@/components/form/lib/Form/Form';
import InputField from '@/components/form/lib/InputField/InputField';
import MaskedField from '@/components/form/lib/InputField/MaskedField';

import { isValidBirthday, isValidName } from '@/util/regexp';

export type SignupStep2FormValues = {
  firstName: string;
  lastName: string;
  birthday: string;
  instagram: string;
  tiktok: string;
};

interface SignupStep2FormProps
  extends Omit<BaseFormProps<SignupStep2FormValues>, 'children' | 'onSubmit'> {
  onSubmit?: (
    values: SignupStep2FormValues,
    actions: FormikHelpers<SignupStep2FormValues>
  ) => void;
}

function isValidDate(date: string): boolean {
  const finalDate = new Date(date);
  const currentDate = new Date();

  return !isNaN(+finalDate) && finalDate < currentDate && isValidBirthday(date);
}

function defaultValidation(
  values: SignupStep2FormValues
): FormikErrors<SignupStep2FormValues> {
  const { firstName, lastName, birthday } = values;
  const errors: FormikErrors<SignupStep2FormValues> = {};

  if (!firstName || !isValidName(firstName)) {
    errors.firstName = 'Please provide your first name';
  }

  if (!lastName || !isValidName(lastName)) {
    errors.lastName = 'Please provide your last name';
  }

  if (!birthday || !isValidDate(birthday)) {
    errors.birthday = 'Birthday must be a valid date in the format mm/dd/yyyy';
  } else if (!isValidBirthday(birthday)) {
    errors.birthday = 'Birthday must be a valid date in the past';
  }

  return errors;
}

export default function SignupStep2Form(props: SignupStep2FormProps) {
  const {
    initialValues,
    validate = defaultValidation,
    onSubmit,
    onSubmitSuccess,
    onSubmitFailure,
    validateOnBlur = true,
    validateOnChange = false
  } = props;

  const handleSubmit = async (values: SignupStep2FormValues) => {
    onSubmitSuccess?.(values /* OptionalDataResponseHere */);
  };

  return (
    <div className='SignupStep2Form'>
      <h1 className='Signup-step-title'>Complete your application</h1>
      <div className='Signup-progress-bar'>
        <div
          className='Progress-bar-fill'
          style={{ width: '40%' }}
        ></div>
      </div>
      <h3 className='Signup-step-subtitle'>My info</h3>

      <Form<SignupStep2FormValues>
        initialValues={initialValues}
        validate={validate}
        // If an 'onSubmit function' is passed in, that function will override
        // this component's 'handleSubmit' function.
        onSubmit={onSubmit || handleSubmit}
        validateOnBlur={validateOnBlur}
        validateOnChange={validateOnChange}
      >
        <div className='Signup-fields-wrapper'>
          <InputField
            className='Signup-first-name'
            name='firstName'
            label={'First Name'}
            type='text'
            data-testid='signup-first-name'
          />
          <InputField
            className='Signup-last-name'
            name='lastName'
            label={'Last Name'}
            type='text'
            data-testid='signup-last-name'
          />
        </div>
        <MaskedField
          className='Signup-birthday'
          guide={false}
          mask={[
            /[0-1]/,
            /[0-9]/,
            '/',
            /[0-3]/,
            /[0-9]/,
            '/',
            /[1-2]/,
            /\d/,
            /\d/,
            /\d/
          ]}
          label={'Birthday'}
          name='birthday'
          placeholder={'mm/dd/yyyy'}
          data-testid='signup-birthday'
        />
        <div className='Signup-fields-wrapper'>
          <InputField
            className='Signup-instagram'
            name='instagram'
            label={'Instagram Handle (OPTIONAL)'}
            data-testid='signup-instagram'
          />
          <InputField
            className='Signup-tiktok'
            name='tiktok'
            label={'TikTok Handle (optional)'}
            data-testid='signup-tiktok'
          />
        </div>
        <button
          className='Signup-Continue-Button cursor'
          type='submit'
          data-testid='signup-step2-continue'
        >
          Continue
        </button>
      </Form>
    </div>
  );
}
